import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Card, CardContent, Typography, Chip, Avatar, Divider, IconButton, Box, Tabs, Tab, TextField, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  PictureAsPdf,
  Image as ImageIcon,
  InsertDriveFile,
  Close as CloseIcon,
  Search,
  CheckCircle
} from '@mui/icons-material';

import UploadFileDialog from './uploadfile';
import { useQuery } from 'react-query';
import API from '../../API';

/**
 * Checks whether the provided filename belongs to an image.
 */
const isImageFile = (filename = '') => {
  const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];
  return imageExtensions.some((ext) => filename.toLowerCase().endsWith(ext));
};

/**
 * Determines the file type based on its extension.
 */
const determineFileType = (filename = '') => {
  if (filename.toLowerCase().endsWith('.pdf')) return 'pdf';
  if (isImageFile(filename)) return 'image';
  // Add additional detections (html, docx, etc.) if needed.
  return 'misc';
};

/**
 * Renders an icon or image preview for the provided file.
 * For images (png, jpeg, etc.) the file_url property is used to render a preview.
 */
const renderFileIcon = (file) => {
  const iconStyle = { fontSize: 36 };
  const fileType = determineFileType(file.filename);

  if (fileType === 'image') {
    return (
      <Avatar
        variant="rounded"
        sx={{
          width: 60,
          height: 60,
          bgcolor: 'transparent',
          boxShadow: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}
        src={file.file_url} // use the file URL to load a thumbnail preview
      >
        <ImageIcon sx={iconStyle} color="primary" />
      </Avatar>
    );
  }

  const iconProps = {
    style: iconStyle,
    color: fileType === 'pdf' ? 'error' : 'action'
  };

  return (
    <Avatar
      variant="rounded"
      sx={{
        width: 60,
        height: 60,
        bgcolor: 'background.paper',
        boxShadow: 1,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      {fileType === 'pdf' ? <PictureAsPdf {...iconProps} /> : <InsertDriveFile {...iconProps} />}
    </Avatar>
  );
};

/**
 * Helper function for filtering by category:
 * - "pdf": show only PDFs
 * - "image": show only images
 * - "misc": everything else
 * - "all": show everything
 */
const filterFilesByCategory = (files = [], category) => {
  if (!files.length) return [];

  if (category === 'all') return files;
  return files.filter((file) => determineFileType(file.filename) === category);
};

/**
 * SelectAssetDialog Component
 * 
 * Props:
 * - open: Boolean flag to control the dialog open state.
 * - onClose: Callback when the dialog is closed.
 * - selectedAsset: Current selected asset.
 * - onAssetClick: Callback when an asset is clicked.
 * - onConfirm: Callback when "Use This File" is clicked with the selected file.
 */
const SelectAssetDialog = ({ open, onClose, selectedAsset, onAssetClick, onConfirm }) => {
  const theme = useTheme();

  // Fetch files (this example uses a single API endpoint)
  const { data: filesResponse, refetch: refetchFiles, isLoading, isFetching } = useQuery(
    'files',
    () => API.files.fetch(''),
    {
      enabled: open,
      refetchOnWindowFocus: false,
      initialData: []
    }
  );

  // Normalize raw file data if needed.
  const files = useMemo(() => {
    if (!Array.isArray(filesResponse)) return [];
    return filesResponse.map((file) => ({
      ...file,
      fileType: determineFileType(file.filename) // e.g., "pdf", "image", "misc"
    }));
  }, [filesResponse]);

  /** Dialog & UI States */
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // for category filtering
  const [searchQuery, setSearchQuery] = useState('');   // for searching
  const [localSelectedAsset, setLocalSelectedAsset] = useState(null);

  /** Handlers */
  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    refetchFiles();
  };

  const handleClose = () => {
    setLocalSelectedAsset(null);
    onClose(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAssetClick = (file) => {
    setLocalSelectedAsset(file);
    if (onAssetClick) {
      onAssetClick(file);
    }
  };

  const handleConfirm = () => {
    if (localSelectedAsset && onConfirm) {
      onConfirm(localSelectedAsset);
    }
    handleClose();
  };

  // First filter by category, then by search query.
  const categoryFilteredFiles = useMemo(() => {
    return filterFilesByCategory(files, activeTab);
  }, [files, activeTab]);
  
  const filteredFiles = useMemo(() => {
    if (!searchQuery) return categoryFilteredFiles;
    const lowerSearch = searchQuery.toLowerCase();
    return categoryFilteredFiles.filter((file) =>
      (file.name || file.filename).toLowerCase().includes(lowerSearch)
    );
  }, [categoryFilteredFiles, searchQuery]);

  // Determine if we're in a loading state
  const showLoading = isLoading || isFetching;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* Title Bar */}
      <DialogTitle
        sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          py: 1.5,
          px: 2
        }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Select an Asset
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Filter Row (Search & Tabs) */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            mb: 2
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            disabled={showLoading}
            sx={{
              flexGrow: 1,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 2
              }
            }}
          >
            <Tab label="All" value="all" />
            <Tab label="PDF" value="pdf" />
            <Tab label="Images" value="image" />
            <Tab label="Misc" value="misc" />
          </Tabs>

          <TextField
            size="small"
            variant="outlined"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={showLoading}
            InputProps={{
              endAdornment: <Search sx={{ color: 'text.secondary' }} />
            }}
          />
        </Box>

        {/* Subtitle + Item Count Chip */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 1
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {activeTab === 'all' ? 'All Files' : activeTab.toUpperCase()}
          </Typography>
          <Chip
            label={showLoading ? 'Loading...' : `${filteredFiles.length} items`}
            size="small"
            sx={{ ml: 'auto', bgcolor: 'primary.dark', color: 'white' }}
          />
        </Box>
      </Box>

      <Divider />

      {/* Dialog Content */}
      <DialogContent sx={{ p: 3, bgcolor: 'background.default', minHeight: 400 }}>
        {showLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 350,
              gap: 2
            }}
          >
            <CircularProgress size={60} thickness={4} />
            <Typography variant="body1" color="text.secondary">
              Loading files...
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredFiles.map((file) => {
              const isSelected = localSelectedAsset?.id === file.id;
              return (
                <Grid item xs={12} sm={6} md={4} key={file.id}>
                  <Card
                    onClick={() => handleAssetClick(file)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : '1px solid transparent',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                        backgroundColor: 'action.hover'
                      },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative'
                    }}
                  >
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1
                        }}
                      >
                        <CheckCircle color="primary" sx={{ fontSize: 28 }} />
                      </Box>
                    )}
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 2,
                        p: 2
                      }}
                    >
                      {renderFileIcon(file)}
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          maxHeight: '3rem'
                        }}
                      >
                        {file.filename}
                      </Typography>
                      <Chip
                        label={(file.fileType || 'FILE').toUpperCase()}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="contained"
          onClick={() => setUploadDialogOpen(true)}
          disabled={showLoading}
          sx={{ mr: 'auto' }}
        >
          Upload New File
        </Button>
        <Button variant="outlined" onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={!localSelectedAsset || showLoading}
          startIcon={<CheckCircle />}
        >
          Use This File
        </Button>
      </DialogActions>

      {/* Upload Dialog */}
      <UploadFileDialog open={uploadDialogOpen} onClose={handleUploadDialogClose} />
    </Dialog>
  );
};

export default SelectAssetDialog;